import React, { useState, useEffect, useCallback } from 'react';
import { getHostels, getBillingConfig, updateBillingConfig, updateBillingProfileImage } from '../api';
import { FiSliders, FiImage, FiSave } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const emptyConfig = {
  billing_mode: 'monthly_flat',
  attendance_tracking_enabled: 0, attendance_affects_billing: 0,
  meals_tracking_enabled: 0, meals_affect_billing: 0, meal_billing_direction: 'add_per_served',
  per_day_rate: 0, absent_deduction_per_day: 0,
  meal_cost_breakfast: 0, meal_cost_lunch: 0, meal_cost_dinner: 0,
  profile_image: null,
};

function Switch({ checked, onChange, label, sub }) {
  return (
    <div className="toggle-row">
      <div>
        <div className="toggle-label">{label}</div>
        {sub && <div className="toggle-sub">{sub}</div>}
      </div>
      <label className="switch">
        <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
        <span className="switch-track"></span>
      </label>
    </div>
  );
}

export default function HostelBilling() {
  const { can } = usePermissions();
  const canEdit = can('hostel_billing', 'edit');
  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState('');
  const [config, setConfig] = useState(emptyConfig);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getHostels().then(res => {
      setHostels(res.data);
      if (res.data.length && !hostelId) setHostelId(res.data[0].id);
    }).catch(() => setError('Failed to load hostels'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!hostelId) return;
    setLoading(true);
    setError(''); setSuccess('');
    try {
      const res = await getBillingConfig(hostelId);
      setConfig({ ...emptyConfig, ...res.data });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load billing config');
    }
    setLoading(false);
  }, [hostelId]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setConfig(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(''); setSuccess('');
    try {
      await updateBillingConfig(hostelId, config);
      setSuccess('Billing configuration saved successfully');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save configuration');
    }
    setSaving(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUri = ev.target.result;
      set('profile_image', dataUri);
      try {
        await updateBillingProfileImage(hostelId, dataUri);
        setSuccess('Profile picture updated — it will now appear on invoices');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to upload picture');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiSliders /> Hostel Billing Configuration</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Set how each hostel's bills are calculated — attendance, meals and rates</p>
        </div>
      </div>

      <div className="form-group" style={{ maxWidth: '360px' }}>
        <label>Hostel</label>
        <select className="form-control" value={hostelId} onChange={e => setHostelId(e.target.value)}>
          {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>

          {/* Profile picture */}
          <div>
            <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', display: 'block' }}>Hostel Profile Picture</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '72px', height: '72px', borderRadius: '12px', overflow: 'hidden', background: '#f7f8fc', border: '1.5px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {config.profile_image ? <img src={config.profile_image} alt="Hostel" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FiImage size={26} color="var(--text-muted)" />}
              </div>
              {canEdit && (
                <label className="btn btn-secondary btn-small" style={{ cursor: 'pointer' }}>
                  Upload Picture
                  <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                </label>
              )}
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Shown on this hostel's invoices</span>
            </div>
          </div>

          {/* Billing mode */}
          <div className="form-group" style={{ marginBottom: 0, maxWidth: '420px' }}>
            <label>Billing Mode</label>
            <select className="form-control" value={config.billing_mode} onChange={e => set('billing_mode', e.target.value)}>
              <option value="monthly_flat">Monthly flat rent (prorated by days)</option>
              <option value="daily_attendance">Daily rate × present days</option>
            </select>
          </div>

          {/* Attendance */}
          <div>
            <Switch
              checked={config.attendance_tracking_enabled}
              onChange={v => set('attendance_tracking_enabled', v ? 1 : 0)}
              label="Track daily attendance"
              sub="Lets hostel & admin mark residents present/absent each day"
            />
            {!!config.attendance_tracking_enabled && (
              <div style={{ marginTop: '8px' }}>
                <Switch
                  checked={config.attendance_affects_billing}
                  onChange={v => set('attendance_affects_billing', v ? 1 : 0)}
                  label="Deduct absences from bill"
                  sub={config.billing_mode === 'daily_attendance'
                    ? 'Not needed — daily mode already only bills present days'
                    : 'Subtract a fixed amount per absent day from the flat rent'}
                />
              </div>
            )}
            {config.billing_mode === 'daily_attendance' && (
              <div className="form-group" style={{ marginTop: '10px', maxWidth: '300px' }}>
                <label>Rate per present day (PKR)</label>
                <input type="number" min="0" className="form-control" value={config.per_day_rate} onChange={e => set('per_day_rate', e.target.value)} />
              </div>
            )}
            {config.billing_mode === 'monthly_flat' && !!config.attendance_tracking_enabled && !!config.attendance_affects_billing && (
              <div className="form-group" style={{ marginTop: '10px', maxWidth: '300px' }}>
                <label>Deduction per absent day (PKR)</label>
                <input type="number" min="0" className="form-control" value={config.absent_deduction_per_day} onChange={e => set('absent_deduction_per_day', e.target.value)} />
              </div>
            )}
          </div>

          {/* Meals */}
          <div>
            <Switch
              checked={config.meals_tracking_enabled}
              onChange={v => set('meals_tracking_enabled', v ? 1 : 0)}
              label="Track daily meals"
              sub="Lets hostel & admin mark breakfast/lunch/dinner served each day"
            />
            {!!config.meals_tracking_enabled && (
              <div style={{ marginTop: '8px' }}>
                <Switch
                  checked={config.meals_affect_billing}
                  onChange={v => set('meals_affect_billing', v ? 1 : 0)}
                  label="Include meals in billing"
                  sub="If off, meals are tracked for information only"
                />
              </div>
            )}
            {!!config.meals_tracking_enabled && !!config.meals_affect_billing && (
              <>
                <div className="form-group" style={{ marginTop: '10px', maxWidth: '420px' }}>
                  <label>How meal cost is applied</label>
                  <select className="form-control" value={config.meal_billing_direction} onChange={e => set('meal_billing_direction', e.target.value)}>
                    <option value="add_per_served">Add cost for every meal served</option>
                    <option value="deduct_per_missed">Rent includes meals — deduct cost for every meal missed</option>
                  </select>
                </div>
                <div className="form-row" style={{ marginTop: '10px', maxWidth: '600px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Breakfast cost (PKR)</label>
                    <input type="number" min="0" className="form-control" value={config.meal_cost_breakfast} onChange={e => set('meal_cost_breakfast', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Lunch cost (PKR)</label>
                    <input type="number" min="0" className="form-control" value={config.meal_cost_lunch} onChange={e => set('meal_cost_lunch', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Dinner cost (PKR)</label>
                    <input type="number" min="0" className="form-control" value={config.meal_cost_dinner} onChange={e => set('meal_cost_dinner', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>

          {canEdit && (
            <div>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <FiSave /> {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
